import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AppRoute } from '@common/app/routes';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import {
  type CompateItemProps,
  type WorkshopUpgradeItemAll,
} from '@common/types/components';
import { type FactoryTypeText } from '@common/types/factory';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { FactoryUpgradeInfo } from '@components/Materials/FactoryUpgradeInfo/FactoryUpgradeInfo';
import { QuarryUpgradeInfo } from '@components/Resources/QuarryUpgradeInfo/QuarryUpgradeInfo';
import { ResourceTitle } from '@components/ResourceTitle/ResourceTitle';
import { H2, TextSmall } from '@components/Typography/Typography';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { MainLinkButton } from '@components/ui/Button/Button';
import { Level } from '@components/ui/Level/Level';
import { MineImage } from '@components/ui/MineImage/MineImage';
import { WorkshopUpgradeInfo } from '@components/Workshop/WorkshopUpgradeInfo/WorkshopUpgradeInfo';
import cn from 'classnames';
import mergeRefs from 'merge-refs';

import css from './styles.module.scss';

interface ConstructSuccessProps {
  kind: 'quarry' | 'factory' | 'workshop';
  id: string;
  level: number;
  name: ResourceTypeText | FactoryTypeText | PlantTypeText;
  compareData?: CompateItemProps[] | WorkshopUpgradeItemAll;
}

const ConstructSucces: FC<ConstructSuccessProps> = ({
  kind,
  id,
  level,
  name,
  compareData,
}) => {
  const { t } = useTranslation();
  const title = getText({ name, kind: 'build', t });
  const ref3 = useHighlightComponentRect<HTMLAnchorElement>(3);
  const ref10 = useHighlightComponentRect<HTMLAnchorElement>(10);
  const ref21 = useHighlightComponentRect<HTMLAnchorElement>(21);
  const bg = getImage({ name, field: 'background' });
  const productionButtonsId =
    kind === 'quarry'
      ? 'intro-step-3'
      : kind === 'factory'
        ? 'intro-step-10'
        : kind === 'workshop'
          ? 'intro-step-21'
          : undefined;

  const getLink = (): string => {
    switch (kind) {
      case 'quarry': {
        return AppRoute.RESOURCES + `/mineResource/${id}`;
      }
      case 'factory': {
        return AppRoute.FACTORY + `/${id}`;
      }
      case 'workshop': {
        return AppRoute.WORKSHOP + `/${id}`;
      }
      default: {
        return '';
      }
    }
  };

  return (
    <main className="h-full w-full">
      <PageBackground
        className="absolute top-0 left-0 h-[61.56vh] z-[-1] dark-gradient"
        backgroundImage={{
          png: bg?.img ?? '',
          webp: bg?.webp ?? '',
        }}
      />
      <div
        className={cn(
          css.buildContent,
          (kind === 'workshop' || kind === 'factory') && 'justify-between',
        )}
      >
        <ResourceTitle className="sm-h:mb-2 mb-10">{title}</ResourceTitle>
        <div
          className={cn(
            'w-full flex flex-col items-center',
            kind === 'quarry' && 'flex-1',
          )}
        >
          {kind === 'quarry' && (
            <div className="flex items-end relative flex-1 w-full justify-center ">
              <MineImage
                className="absolute bottom-0 left-1/2 -translate-x-1/2 aspect-square w-full min-h-0"
                resourceName={name as ResourceTypeText}
              />
            </div>
          )}
          <div className="flex flex-col items-center">
            <Level className="mb-4" value={level} variant="default" />
            <H2 weight="bold" className="text-white mb-2">
              {t(level === 1 ? 'Building constructed' : 'Building upgraded')}
            </H2>
            <TextSmall
              weight="regular"
              className="text-white/60 md-h:mb-4 mb-7"
            >
              {t('Keep up the great work!')}
            </TextSmall>
          </div>
          {kind === 'quarry' && (
            <QuarryUpgradeInfo id={id} className="md-h:!mb-6 !mb-8" />
          )}
          {kind === 'workshop' && (
            <WorkshopUpgradeInfo
              data={compareData as WorkshopUpgradeItemAll | undefined}
              id={id}
            />
          )}
          {kind === 'factory' && (
            <FactoryUpgradeInfo
              list={compareData as CompateItemProps[] | undefined}
              id={id}
              withoutNext={level === 0 || level === 1}
            />
          )}
          <MainLinkButton
            ref={mergeRefs(ref3, ref10, ref21)}
            id={productionButtonsId}
            to={getLink()}
            text={t(kind === 'quarry' ? 'Mine' : 'Production')}
          />
        </div>
      </div>
    </main>
  );
};

export { ConstructSucces };
